/* ------------------------------------------------------------------ */
/*  Faith 1st — shared content data                                   */
/*  Copy is owned in one place so interior pages can compose freely.   */
/* ------------------------------------------------------------------ */

export const navLinks = [
  "Who We Help",
  "How We Help",
  "Process",
  "Speaking",
  "About",
  "Insights",
  "Contact",
];

export const fitSignals = [
  "Mission-driven nonprofits",
  "Foundations & associations",
  "Faith-based organizations",
  "Mission-driven employers",
];

export const problemPatterns = [
  {
    tag: "Leadership pressure",
    title: "The leader who stopped sleeping but won’t name why.",
    body:
      "Carrying a transition, board tension, or sensitive personnel question alone — with no obvious person to bring it to.",
  },
  {
    tag: "Supervisory exposure",
    title: "The supervisor doing their best, exposed every day.",
    body:
      "Promoted on competence. Asked to lead people. Never trained for the conversations the role actually requires.",
  },
  {
    tag: "Conduct & culture",
    title: "The team that learned to work around a conduct issue.",
    body:
      "Trust quietly draining while everyone stays professional. Culture work feels too vague; legal feels too sharp.",
  },
  {
    tag: "Scale & systems",
    title: "The organization growing faster than its people practices.",
    body:
      "New programs, new hires, same informal systems. Founders or EDs holding too much institutional memory alone.",
  },
];

export const supportArchitectures = [
  {
    n: "01",
    label: "Advisory",
    body:
      "Ongoing executive counsel for sensitive people, leadership, and culture decisions.",
  },
  {
    n: "02",
    label: "Supervisory readiness",
    body:
      "Practical, HR-aware development for the managers carrying the most operational weight.",
  },
  {
    n: "03",
    label: "Coaching & facilitation",
    body:
      "Quiet support for the individual leader or the room that requires careful, confidential handling.",
  },
];

export const pathways = [
  {
    label: "Leadership & People Advisory",
    descriptor:
      "An ongoing trusted thinking partner on the sensitive people and leadership decisions executives cannot fully discuss internally.",
    bestWhen: "When the stakes are real and the path forward isn’t obvious.",
    primary: true,
  },
  {
    label: "Supervisory Training & Readiness",
    descriptor:
      "Practical, HR-aware development that equips managers with judgment, language, and structure for the conversations the role actually requires.",
    bestWhen: "When managers are inconsistent, exposed, or under-trained.",
    primary: true,
  },
  {
    label: "Coaching for Leaders",
    descriptor:
      "Quiet, candid executive coaching for leaders carrying real organizational weight.",
    bestWhen: "When an executive needs an outside thinking partner, not a curriculum.",
    primary: false,
  },
  {
    label: "Sensitive Facilitation",
    descriptor:
      "Steady facilitation when conduct, culture, or trust requires careful, confidential handling.",
    bestWhen: "When the room is too delicate for HR alone and too human for legal.",
    primary: false,
  },
  {
    label: "Speaking & Authority Engagements",
    descriptor:
      "Authority content brought into boards, leadership teams, and mission-aligned audiences.",
    bestWhen: "For boards, leadership offsites, and association gatherings.",
    primary: false,
  },
];

export const inquiryFlow = [
  {
    n: "01",
    label: "Inquiry",
    body:
      "You share context through the inquiry form. Identifying information stays with the practice only.",
  },
  {
    n: "02",
    label: "Response within 2 business days",
    body:
      "We acknowledge the inquiry and propose a brief discovery conversation — or, if we’re not the right fit, point you elsewhere.",
  },
  {
    n: "03",
    label: "Discovery conversation",
    body:
      "Thirty minutes. We listen, name what we’re hearing, and decide together whether to continue. No deck. No pitch.",
  },
  {
    n: "04",
    label: "Proposal & scope",
    body:
      "If we’re a fit, we co-design the engagement — scope, sequencing, deliverables, and the rhythm of the work.",
  },
  {
    n: "05",
    label: "Onboarding & engagement",
    body:
      "Work begins, held with discretion, within the organization’s rhythms. We leave behind capability, not dependency.",
  },
];

export const disciplines = [
  "Legal-informed",
  "HR-aware",
  "Coaching-centered",
  "Facilitation-skilled",
  "Mission-fluent",
  "Discreet",
];

export const livedComplexity = [
  "Mission-driven organizations of 50–500 people",
  "Boards navigating leadership transitions",
  "Senior teams under reputational or conduct strain",
  "HR functions carrying more than the structure supports",
];

export const inquirySubjects = [
  "Leadership & People Advisory",
  "Manager & Supervisor Development",
  "Executive Coaching",
  "Workplace Investigations & Culture Support",
  "Speaking & Keynotes",
  "Not yet sure — exploring fit",
];

export const inquiryTimings = [
  "Immediate — already in the moment",
  "Within the next month",
  "Within the next quarter",
  "Exploring for a future engagement",
];

export const inquiryAssurances = [
  ["Discretion", "Identifying details stay with the practice only — never shared, never published."],
  ["No incident narratives", "Please keep descriptions brief. Sensitive details are surfaced safely in the discovery conversation."],
  ["Direct response", "Every inquiry is answered by Katrina directly."],
];

export const footerColumns = [
  ["Practice", ["Who We Help", "How We Help", "Process", "Speaking"]],
  ["About", ["Katrina Bernard", "Approach", "Insights"]],
  ["Connect", ["Consultation", "Referral", "LinkedIn"]],
];


/* ------------------------------------------------------------------ */
/*  CONCEPT 5 — final production content                              */
/*  Merged direction: composed warmth, disciplined layout, six-pathway */
/*  architecture, thought-leadership section, consultation-led CTA.    */
/* ------------------------------------------------------------------ */

/**
 * Concept 5 brand identity and positioning.
 *
 * Faith 1st is a Leadership, People & Culture Consulting firm.
 * Not "executive advisory" — advisory is a capability within the practice,
 * not the identity. Katrina is the credibility layer inside Faith 1st,
 * not the whole brand.
 */
export const brand5 = {
  name: "Faith 1st",
  category: "Leadership, People & Culture Consulting",
  markSubtitle: "Leadership, People & Culture",
  fullLine: "Faith 1st | Leadership, People & Culture Consulting",
};

/**
 * Approved positioning: hero headline + subhead + CTAs.
 *
 * Headline is the single-line, buyer-and-stakes-forward statement that
 * names the category and the moment in one breath.
 */
export const positioning5 = {
  eyebrow: "Consulting Practice · Faith 1st",
  primary:
    "Practical consulting support for the people, culture, and leadership challenges mission-driven organizations cannot afford to mishandle.",
  supporting:
    "Faith 1st is a Leadership, People & Culture Consulting firm for mission-driven and values-led organizations — a steady outside partner for executive, HR, and board-level leaders navigating leadership transitions, workplace conduct concerns, management capability gaps, and the moments where trust, culture, and performance are all at stake at once.",
  primaryCta: "Book a consultation",
  secondaryCta: "Start a confidential conversation",
};

/**
 * Compact proof band that sits IMMEDIATELY below the hero.
 *
 * Four verifiable trust signals rendered as a horizontal row on a
 * warm Dry Stone surface. Establishes authority above the fold so
 * the right buyer doesn't have to scroll to recognize seriousness.
 */
export const proofBand5 = [
  {
    label: "Two decades",
    body: "at the intersection of employment law, senior HR, coaching, and facilitation.",
  },
  {
    label: "Mission-driven",
    body: "nonprofits, foundations, associations, and faith-led organizations of 50–500 people.",
  },
  {
    label: "Board & executive level",
    body: "trusted counsel for executive directors, boards, and senior People leaders.",
  },
  {
    label: "Discreet by design",
    body: "every inquiry confidential; every engagement scoped and held with care.",
  },
];

/**
 * The Faith 1st approach — the "how we work" method chamber that
 * sits between problem recognition and services.
 *
 * Three disciplined moves that describe what consulting actually
 * looks like inside Faith 1st, so buyers can decide before scanning
 * offer names.
 */
export const approach5 = {
  eyebrow: "The Faith 1st approach",
  headline: "Practical, senior, and held with care.",
  intro:
    "Faith 1st is a consulting practice — not a training vendor, not a coach roster, not a keynote circuit. Every engagement follows the same three disciplines, sized to the moment your organization is in.",
  moves: [
    {
      n: "01",
      label: "We listen for the actual moment.",
      body:
        "Before we scope anything, we take the time to hear what is really happening — the terrain beneath the presenting problem — so the work is built around your organization, not a template.",
    },
    {
      n: "02",
      label: "We bring senior judgment into the room.",
      body:
        "Two decades of employment-law fluency, senior HR practice, certified executive coaching, and sensitive facilitation — brought as one consulting judgment, not four disconnected specialties.",
    },
    {
      n: "03",
      label: "We leave capability, not dependency.",
      body:
        "Every engagement is designed to leave your leaders, managers, and People function stronger than we found them — with the language, structure, and rehearsed conversations they can carry forward.",
    },
  ],
};

/**
 * Navigation for Concept 5. "Services" replaces "Pathways" to match the
 * consulting posture.
 */
export const navLinks5 = [
  "Services",
  "About",
  "Process",
  "Insights",
  "Speaking",
  "Contact",
];

/**
 * Four consulting-forward services — Faith 1st's core architecture.
 *
 * The pathways are named the way executive, HR, and board-level buyers
 * search for them — with "Consulting" as the visible discipline, not
 * "advisory" or "practice" competing with the label.
 *
 *   01. Leadership & People Advisory
 *   02. Manager & Supervisor Development
 *   03. Executive Coaching
 *   04. Workplace Investigations & Culture Support
 */
export const pathways5 = [
  {
    n: "01",
    label: "Leadership & People Advisory",
    descriptor:
      "An outside consulting partner for executive directors, boards, and senior People leaders navigating leadership transitions, proactive HR strategy, handbook revisions, embedded interim support, or the moment where the internal team simply needs a steadier voice in the room.",
    bestWhen:
      "When the stakes are real, the terrain is sensitive, and there is no obvious person inside the organization to bring it to.",
  },
  {
    n: "02",
    label: "Manager & Supervisor Development",
    descriptor:
      "Practical, HR-aware training that equips supervisors and mid-level managers with the judgment, language, and rehearsed conversations the role actually requires — performance direction, conduct correction, development pacing, and the ambient work of setting a floor.",
    bestWhen:
      "When managers were promoted on competence but never trained for the conversations the role requires.",
  },
  {
    n: "03",
    label: "Executive Coaching",
    descriptor:
      "Confidential 1:1 coaching for executive directors, senior leaders, and rising leaders carrying complex people, culture, or transition responsibilities. Strengths-based; Gallup-trained CliftonStrengths integrated where useful.",
    bestWhen:
      "When a leader needs an outside thinking partner who has seen this terrain before — not a curriculum.",
  },
  {
    n: "04",
    label: "Workplace Investigations & Culture Support",
    descriptor:
      "Contained support for the moments where trust has quietly drained, conduct has been worked around, or leadership needs a clear read on what is actually happening beneath the professional surface. For select clients, Faith 1st Consulting also conducts workplace investigations and complex conduct reviews — with discretion, trauma-aware interviewing, and clear recommendations for boards and executive teams.",
    bestWhen:
      "When the room is too delicate for HR alone and too human for outside counsel.",
  },
];

/**
 * Six trust signals rendered as an architectural attribute grid.
 */
export const trustAttributes = [
  {
    label: "Law-informed",
    body: "Two decades of employment-law fluency behind every conversation.",
  },
  {
    label: "HR-aware",
    body: "Senior HR judgment — the kind that lives inside organizations, not outside them.",
  },
  {
    label: "Coaching-centered",
    body: "Certified executive coach and Gallup-trained CliftonStrengths practitioner.",
  },
  {
    label: "Mission-aligned",
    body: "Built for mission-driven and values-led organizations and the leaders inside them.",
  },
  {
    label: "Discreet",
    body: "Every inquiry is confidential. Every engagement is held with care.",
  },
  {
    label: "Facilitation-skilled",
    body: "Steady in the rooms where culture, conduct, and trust are all at stake.",
  },
];

/**
 * Thought-leadership publications. Realistic placeholders that
 * demonstrate a formal, white-paper-style posture (not blog).
 */
export const publications = [
  {
    tag: "White Paper",
    date: "2026 · 01",
    title: "The Silent Cost of Untrained Supervisors",
    kicker:
      "Why promoted-on-competence managers cost mission-driven organizations more than turnover data suggests — and the readiness architecture that recovers it.",
    length: "White paper · 24 pages",
  },
  {
    tag: "Practice Note",
    date: "2025 · 12",
    title: "Navigating Board Transitions Under Reputational Strain",
    kicker:
      "A framework for executive directors and board chairs holding sensitive personnel decisions while the organization is publicly watched.",
    length: "Practice note · 12 pages",
  },
  {
    tag: "Field Report",
    date: "2025 · 11",
    title: "When Culture Work Feels Too Vague and Legal Feels Too Sharp",
    kicker:
      "The third path most organizations don’t know they need — where HR alone is not enough and outside counsel is the wrong instrument.",
    length: "Field report · 8 pages",
  },
  {
    tag: "Guide",
    date: "2025 · 09",
    title: "The First Thirty Days of a New Head of People",
    kicker:
      "A practical arrival playbook for senior HR leaders stepping into organizations already under active pressure.",
    length: "Guide · 16 pages",
  },
];

/**
 * The four recognition patterns — tightened for the calmer Concept 5
 * register and rewritten to name concrete buyer scenarios (leadership
 * transitions, workplace conduct, supervisory exposure, growing
 * People function pressure) rather than abstractions.
 */
export const patterns5 = [
  {
    n: "01",
    tag: "Leadership transition",
    title: "A senior transition is coming and no one has said it out loud.",
    body:
      "A founder stepping back, a long-tenured ED leaving, a Head of People arriving. The board and executive team need a steady outside voice to sequence the legal, reputational, and human conversations without confusing them.",
  },
  {
    n: "02",
    tag: "Supervisory exposure",
    title: "Managers were promoted on competence — never trained for the role.",
    body:
      "Performance conversations don't happen, or happen badly. Conduct issues get worked around. The cost accumulates quietly in trust, retention, and mission outcomes long before it shows up in turnover data.",
  },
  {
    n: "03",
    tag: "Conduct & culture",
    title: "A team has learned to work around a conduct issue.",
    body:
      "Trust is quietly draining while everyone stays professional. Culture consultants feel too vague; outside counsel feels too sharp. The organization needs the third path — contained, HR-aware, human.",
  },
  {
    n: "04",
    tag: "People function under pressure",
    title: "The People function is carrying more than the structure supports.",
    body:
      "Handbook revisions overdue. HR practices built on institutional memory alone. A senior People leader needing a steady thinking partner, or an embedded outside voice on a defined engagement, until the internal footing is rebuilt.",
  },
];

/**
 * Five-step engagement process — the "how it begins" architecture.
 */
export const process5 = [
  {
    n: "01",
    label: "Inquiry",
    body:
      "You share context through the inquiry form. Identifying information stays with the practice only.",
  },
  {
    n: "02",
    label: "Response within 2 business days",
    body:
      "We acknowledge the inquiry and propose a brief discovery conversation — or point you elsewhere if we’re not the right fit.",
  },
  {
    n: "03",
    label: "Discovery conversation",
    body:
      "Thirty minutes. We listen, name what we’re hearing, and decide together whether to continue. No deck. No pitch.",
  },
  {
    n: "04",
    label: "Proposal & scope",
    body:
      "If we’re a fit, we co-design the engagement — scope, sequencing, deliverables, and the rhythm of the work.",
  },
  {
    n: "05",
    label: "Onboarding & engagement",
    body:
      "Work begins, held with discretion, within the organization’s rhythms. We leave behind capability, not dependency.",
  },
];

/**
 * Consultation eyebrow and CTA copy for the closing chamber.
 * Aligned to the "Book a consultation" primary CTA.
 */
export const consultation5 = {
  eyebrow: "Book a consultation",
  headline: "Tell us about the moment your organization is in.",
  body:
    "The website's job is to help the right buyer recognize a fit fast. If Faith 1st sounds close to what your organization needs, a short, deliberate inquiry is enough — Katrina reads every submission personally, and we respond within two business days.",
};

/**
 * Inquiry qualifier fields for the intake form.
 *
 * The form now screens for organization type, team size, and timing
 * in addition to the challenge area — so the intake matches the
 * qualified-conversion posture rather than gathering anonymous
 * inbound.
 */
export const organizationTypes = [
  "Nonprofit or mission-driven organization",
  "Foundation or philanthropy",
  "Association or membership body",
  "Faith-based organization",
  "Values-led private employer",
  "Government or public-sector",
  "Other",
];

export const teamSizes = [
  "Fewer than 50 people",
  "50 – 150 people",
  "150 – 500 people",
  "500 – 1,000 people",
  "1,000+ people",
];

/* ------------------------------------------------------------------ */
/*  CONCEPT 5 — interior page content                                  */
/*  Slug maps + long-form content for interior page templates.         */
/* ------------------------------------------------------------------ */

/**
 * URL-friendly slugs for each pathway. The pathway detail page reads
 * `params.slug` and matches against this map.
 */
export const pathwaySlugs = {
  "leadership-people-advisory": "01",
  "manager-supervisor-development": "02",
  "executive-coaching": "03",
  "workplace-investigations-culture-support": "04",
};

/**
 * Extra content for each service used on the service detail page.
 * Keyed by service number (matches pathways5[i].n).
 */
export const pathwayDetails = {
  "01": {
    slug: "leadership-people-advisory",
    engagementShape: [
      "Discovery of the current people, leadership, and HR terrain",
      "Consulting on the specific situation — transition, handbook, People strategy, or interim leadership gap",
      "Working sessions with the executive team, board, or People function as scoped",
      "Written recommendations and playbooks the internal team can carry forward",
    ],
    whoItsFor:
      "Mission-driven and values-led organizations navigating leadership transitions, proactive HR strategy, handbook revisions, part-time embedded HR leadership, or the moments where the internal team needs a steadier outside voice than generic consulting or traditional HR support can provide.",
    engagementLength: "3 – 12 months typical",
    outcome:
      "The organization moves through the situation with a steadier hand, clearer language, and a practical plan the internal team can actually run.",
  },
  "02": {
    slug: "manager-supervisor-development",
    engagementShape: [
      "Assessment of current supervisory capability and exposure",
      "Workshop or cohort-based development designed for your organization",
      "Real-conversation practice — the conversations supervisors are avoiding",
      "Written playbook the organization keeps after the engagement ends",
    ],
    whoItsFor:
      "Organizations whose supervisors were promoted on competence, were never trained for the conversations the role requires, and are quietly creating exposure the leadership team hasn't yet named — performance direction, conduct correction, development pacing.",
    engagementLength: "Half-day workshop through 6-month cohort",
    outcome:
      "Supervisors who can hold performance conversations, name conduct issues, run development conversations, and support their teams without escalating every difficult moment upward.",
  },
  "03": {
    slug: "executive-coaching",
    engagementShape: [
      "Confidential 1:1 coaching engagement, monthly or bi-weekly",
      "CliftonStrengths assessment and integration (Gallup-trained)",
      "Between-session written thinking partnership as agreed",
      "Optional 360-degree feedback within the leader's system",
    ],
    whoItsFor:
      "Executive directors, senior leaders, and rising leaders carrying complex people, culture, or transition responsibilities — who need an outside thinking partner, not a curriculum.",
    engagementLength: "6 – 12 months typical",
    outcome:
      "Clearer sense of how you lead best, better decisions under pressure, and a sustained partner who has seen enough of your organization to be genuinely useful.",
  },
  "04": {
    slug: "workplace-investigations-culture-support",
    engagementShape: [
      "Contained discovery — one-on-ones and small-group listening",
      "A read on what's actually happening beneath the professional surface",
      "For select engagements: workplace investigations with trauma-aware interviewing and formal findings",
      "Facilitated sessions with the right people, on the right terms — plus a written summary of what was said and what should happen next",
    ],
    whoItsFor:
      "Teams and organizations where trust has quietly drained, where a conduct issue has been worked around for too long, or where boards and executive teams need a clear, defensible read on what is actually happening before deciding what to do about it.",
    engagementLength: "6 – 16 weeks typical",
    outcome:
      "Leadership has a clear, defensible picture of what happened and what to do next. Trust either recovers deliberately, or the organization makes an honest, informed decision about what comes next.",
  },
};

/**
 * Slugs and long-form article bodies for the four publications.
 */
export const publicationSlugs = {
  "silent-cost-untrained-supervisors": 0,
  "board-transitions-reputational-strain": 1,
  "culture-work-vague-legal-sharp": 2,
  "first-thirty-days-head-of-people": 3,
};

export const publicationBodies = [
  {
    slug: "silent-cost-untrained-supervisors",
    pullQuote:
      "Turnover data is a lagging indicator. By the time the pattern is legible in the spreadsheet, the trust it measures has been eroded for eighteen months.",
    sections: [
      {
        heading: "The pattern most organizations miss",
        body:
          "Mission-driven organizations promote on competence — the strongest program manager becomes the program director; the most reliable individual contributor becomes the team lead. What they inherit is a role built around conversations they were never trained to hold: performance direction, conduct correction, development pacing, and the ambient work of setting a floor. When those conversations don't happen — or happen badly — the cost accumulates in ways the leadership team doesn't yet see.",
      },
      {
        heading: "What the cost actually looks like",
        body:
          "It looks like a program manager who took eight months to name that a team member wasn't performing, because they didn't know how. It looks like a director who over-relies on their most capable staff member and under-manages everyone else, because directness feels risky. It looks like an executive director who quietly stops raising performance issues in her one-on-ones because the manager below her doesn't act on them. And underneath all of it, the mission suffers — not dramatically, not visibly, but consistently.",
      },
      {
        heading: "A readiness architecture that recovers it",
        body:
          "The organizations that recover this cost don't do it through a single training. They build a readiness architecture: a set of language, structure, and rehearsed conversations that managers actually use. Then they build a rhythm around it — quarterly check-ins with the practice, not annual workshops. And they make clear that being a manager here means being trained as one, continuously, for as long as you hold the role.",
      },
    ],
  },
  {
    slug: "board-transitions-reputational-strain",
    pullQuote:
      "The hardest board transitions are not the ones that happen under a crisis. They are the ones that happen while the crisis is still under discussion, in rooms that don't have a name yet.",
    sections: [
      {
        heading: "The context this paper addresses",
        body:
          "When an organization is publicly watched — because of its mission, its funders, its recent history, or the specific composition of its leadership — board transitions carry a weight that ordinary succession frameworks don't fully hold. Board chairs and executive directors making sensitive personnel decisions in these moments are typically navigating three simultaneous conversations: legal, reputational, and human. Confusing them is the most common failure mode.",
      },
      {
        heading: "A framework for the three conversations",
        body:
          "This paper offers a framework for holding those conversations distinctly, in the right rooms, on the right timelines. The legal conversation belongs with counsel and is bounded. The reputational conversation belongs with communications and is shaped by the audiences that matter. The human conversation belongs with the person and their circumstances — and it is the one most often handled last, when it should be handled first.",
      },
      {
        heading: "What we've learned from twenty of them",
        body:
          "Faith 1st has advised on twenty transitions of this shape. The consistent finding is that the organizations that come through them with their trust intact are the ones that took the time to sequence the conversations — not because they had a script, but because they had a private space to think out loud before they acted.",
      },
    ],
  },
  {
    slug: "culture-work-vague-legal-sharp",
    pullQuote:
      "Legal is the wrong instrument for a culture problem. Culture work is the wrong instrument for a legal one. Most organizations reach for both when they should be reaching for the third path.",
    sections: [
      {
        heading: "The third path most organizations don't know they need",
        body:
          "There is a specific category of organizational moment that HR alone can't hold and outside counsel shouldn't be asked to. It's the moment where a team has learned to work around a conduct issue, where trust has quietly drained, where the professional surface is intact but something underneath has broken. Culture consultants tend to arrive with frameworks that feel too vague; employment counsel arrives with instruments that feel too sharp. Both are wrong for the moment.",
      },
      {
        heading: "What the third path looks like",
        body:
          "It looks like contained discovery, careful listening, facilitated conversations with the right people, and a written record of what was said and what should happen next. It is neither a mediation nor an investigation. It is a form of sensitive facilitation — legally informed, HR-aware, human — that gets the room to say what it hasn't been able to say and gives leadership a clear picture of what to do.",
      },
    ],
  },
  {
    slug: "first-thirty-days-head-of-people",
    pullQuote:
      "The first thirty days determine whether the organization ever hears the actual truth about itself, or a curated version. Most Heads of People don't know they only have that window.",
    sections: [
      {
        heading: "Why the first thirty days matter more than they appear to",
        body:
          "In your first thirty days as Head of People, the organization is deciding — mostly unconsciously — whether to trust you with the real story of itself. If they trust you, the rest of the role becomes possible. If they don't, everything after is a curated version of what's actually happening, and you spend the next two years trying to hear the truth through third parties.",
      },
      {
        heading: "The arrival playbook",
        body:
          "This guide walks through the deliberate architecture of a strong first thirty days: the listening tour, the calibration meetings, the early signals to send about how you'll handle confidence, the two or three visible actions that establish your posture, and the private rhythms with the executive that make everything else workable. It is not a checklist — it is a set of design decisions, sequenced for the terrain most Heads of People inherit.",
      },
    ],
  },
];

/**
 * Katrina Bernard — long-form biography for the About page.
 *
 * Framed as the credibility layer inside Faith 1st, not the whole
 * brand. Opens on the philosophy line ("Faith in people, excellence
 * in culture.") and includes a "brought in when…" sentence so the
 * page reads as an institutional trust module rather than a bio block.
 */
export const katrinaBio = {
  eyebrow: "Founder & Principal",
  philosophy: "Faith in people, excellence in culture.",
  intro:
    "Faith 1st is a Leadership, People & Culture Consulting firm for mission-driven and values-led organizations. Katrina Bernard, JD | CPC, is its founder and principal — brought in when leadership transitions, workplace conduct concerns, management capability gaps, or People-function pressure need senior outside judgment held with care.",
  paragraphs: [
    "Katrina spent two decades inside organizations before founding Faith 1st. She has served as senior HR counsel, chief of staff, and senior-level consultant across mission-driven nonprofits, foundations, associations, faith-based institutions, and values-led employers of 50–500 people. That interior experience — held with discretion — is what the practice is built on.",
    "Faith 1st sits at the intersection of employment law, senior HR judgment, certified executive coaching, and sensitive facilitation. Katrina is a licensed attorney (JD), a certified professional coach (CPC), and a Gallup-trained CliftonStrengths practitioner. She has held employment-law counsel, senior HR leadership, and board consulting roles.",
    "The practice writes and speaks on the specific terrain most organizational buyers navigate without shared vocabulary: supervisory readiness, conduct and culture work, board transitions under reputational strain, and the architecture of a People function that behaves like it belongs inside the institution.",
    "Katrina lives in the Washington, D.C. area; Faith 1st works with clients across the United States. Engagements are accepted on a limited basis each quarter to protect the depth the work requires.",
  ],
  broughtInWhen: [
    "Leadership transitions the board needs to sequence carefully",
    "Workplace conduct concerns that HR alone cannot hold",
    "Management capability gaps costing the mission quietly",
    "People-function pressure — handbook revisions, embedded interim leadership, HR strategy",
  ],
  credentials: [
    ["Juris Doctor (JD)", "Employment law focus"],
    ["Certified Professional Coach (CPC)", "ICF-credentialed"],
    ["Gallup-trained CliftonStrengths", "Strengths-based coaching"],
    ["Senior HR credentialing", "SPHR / SHRM-SCP eligible"],
    ["Board consulting experience", "Mission-driven organizations"],
    ["Sensitive facilitation training", "Culture, conduct, and trust work"],
  ],
  affiliations: [
    "Society for Human Resource Management (SHRM)",
    "International Coaching Federation (ICF)",
    "American Bar Association — Labor & Employment Section",
    "National Council of Nonprofits",
  ],
};

/**
 * Speaking page content — aligned to "Speaking & Keynotes."
 */
export const speakingContent = {
  eyebrow: "Speaking & Keynotes",
  headline: "Keynotes and talks for boards, leadership teams, and mission-aligned audiences.",
  intro:
    "Faith 1st brings keynotes and talks that help audiences think differently about leadership, workplace culture, accountability, and people-centered management. Every engagement is designed for the specific room.",
  topics: [
    {
      n: "01",
      title: "The Silent Cost of Untrained Supervisors",
      body:
        "For leadership teams and boards ready to name what promoted-on-competence supervisors are quietly costing the mission — and what supervisory readiness actually looks like.",
      format: "Keynote · 60 min or workshop · 3 hrs",
    },
    {
      n: "02",
      title: "Board Transitions Under Reputational Strain",
      body:
        "For boards and executive directors sequencing the legal, reputational, and human conversations that difficult transitions require.",
      format: "Facilitated session · 2 hrs",
    },
    {
      n: "03",
      title: "The Third Path — When Culture Feels Vague and Legal Feels Sharp",
      body:
        "For HR leaders, EDs, and general counsel navigating conduct-and-culture moments that don't fit either instrument.",
      format: "Keynote · 45 min",
    },
    {
      n: "04",
      title: "Arrival Playbook for Senior HR Leaders",
      body:
        "For newly appointed Heads of People and the executives who hired them — designing the first ninety days deliberately.",
      format: "Workshop · half day",
    },
    {
      n: "05",
      title: "Custom sessions",
      body:
        "Content designed for your board, leadership team, or association on the specific terrain your organization is in.",
      format: "By engagement",
    },
  ],
  engagements: [
    "Independent School Association · leadership offsite · 2025",
    "Regional Community Foundation · board development day · 2025",
    "National Faith-Based Executives Roundtable · closing keynote · 2024",
    "Mission-Driven CEO Circle · off-the-record session · 2024",
    "State Nonprofit Association · annual conference · 2024",
  ],
};

